import { LinearProgress, Box } from "@mui/material";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";

import { Z_INDEX } from "@/constants/zIndex";

/**
 * FEEDBACK WHILE A ROUTE IS LOADING.
 *
 * Every screen in this app is server-rendered (`getServerSideProps`), so a
 * navigation is a network round trip before anything changes on screen. Next
 * keeps the CURRENT page mounted for the whole of it, which is usually fine —
 * except when the page you are leaving has nothing on it. Pressing "Back to
 * tables" from an order screen that failed to load its order left the operator
 * staring at an empty screen with no sign that anything was happening, which
 * is the "blank order screen" they reported. On a slow connection in a shop
 * that wait is seconds long, and the natural response is to press again.
 *
 * So: a determinate-looking bar across the top for the length of the
 * transition, plus a click shield so a second press cannot queue a second
 * navigation. Nothing is hidden and no layout moves — the bar is fixed and
 * two pixels tall.
 *
 * `routeChangeError` matters as much as `routeChangeComplete`: a cancelled or
 * failed navigation must clear the bar, or the app looks permanently busy.
 */
export default function RouteTransition() {
  const router = useRouter();
  const [active, setActive] = useState(false);

  useEffect(() => {
    // A same-page navigation (a query-string change the page handles itself,
    // e.g. the floor plan writing ?table_id=) is not a load and must not
    // flash the bar.
    const start = (url: string, { shallow }: { shallow: boolean }) => {
      if (!shallow) setActive(true);
    };
    const stop = () => setActive(false);

    router.events.on("routeChangeStart", start);
    router.events.on("routeChangeComplete", stop);
    router.events.on("routeChangeError", stop);
    return () => {
      router.events.off("routeChangeStart", start);
      router.events.off("routeChangeComplete", stop);
      router.events.off("routeChangeError", stop);
    };
  }, [router.events]);

  if (!active) return null;

  return (
    <Box
      aria-hidden
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: Z_INDEX.LOADING,
        // Transparent: the page behind stays readable. This exists to swallow
        // the second tap, not to cover anything up.
        pointerEvents: "auto",
      }}
    >
      <LinearProgress
        color="primary"
        sx={{ height: 3, position: "fixed", top: 0, left: 0, right: 0 }}
      />
    </Box>
  );
}
